import { useState, useEffect, useRef, useCallback } from 'react'
import * as React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Loader2, Wallet, CreditCard, Store, Camera, ArrowLeft, RotateCcw, Landmark, Copy } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { loadStripe } from '@stripe/stripe-js'
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js'
import { exchangeRateApi } from '@/services/exchangeRateApi'
import { apiClient } from '@/services/api'
import { formatCurrency, normalizeCurrencyCode } from '@/lib/utils'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { Select } from '@/components/ui/Select'
import { CreateWalletModal } from '@/components/walletpage/CreateWalletModal'
import toast from 'react-hot-toast'

const stripePublishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || import.meta.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY || ''
const stripePromise = stripePublishableKey ? loadStripe(stripePublishableKey) : null

interface AddMoneyModalProps {
  isOpen: boolean
  onClose: () => void
  userCurrency: string
  wallets?: any[]
  onSuccess?: () => void
}

type PaymentMethod = 'select' | 'stripe' | 'local_store' | 'mb_reference'

interface StripePaymentFormProps {
  wallets?: any[]
  onSuccess: () => void
  onClose: () => void
  onBack: () => void
}

function StripePaymentForm({ wallets, onSuccess, onClose, onBack }: StripePaymentFormProps) {
  const { t } = useTranslation()
  const stripe = useStripe()
  const elements = useElements()
  const [amount, setAmount] = useState<string>('')
  const [selectedCurrency, setSelectedCurrency] = useState<'EUR' | 'USD'>('EUR')
  const [selectedWalletId, setSelectedWalletId] = useState<string>('')
  const [stdAmount, setStdAmount] = useState<number>(0)
  const [exchangeRate, setExchangeRate] = useState<number>(1)
  const [feeSchedule, setFeeSchedule] = useState<any | null>(null)
  const [feeAmount, setFeeAmount] = useState<number>(0)
  const [netAmount, setNetAmount] = useState<number>(0)
  const [isFeeLoading, setIsFeeLoading] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isConverting, setIsConverting] = useState(false)
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null)

  // Set default wallet if wallets available
  React.useEffect(() => {
    if (wallets && wallets.length > 0 && !selectedWalletId) {
      setSelectedWalletId(wallets[0].id)
    }
  }, [wallets, selectedWalletId])

  const calculateFee = useCallback((amountValue: number, schedule: any | null) => {
    if (!schedule || amountValue <= 0) {
      return 0
    }

    const percent = Number(schedule.cash_in_percent || 0) / 100
    const fixed = Number(schedule.cash_in_fixed || 0)
    const minFee = schedule.min_fee !== null ? Number(schedule.min_fee) : null
    const maxFee = schedule.max_fee !== null ? Number(schedule.max_fee) : null

    let fee = amountValue * percent + fixed
    if (minFee !== null && fee < minFee) {
      fee = minFee
    }
    if (maxFee !== null && fee > maxFee) {
      fee = maxFee
    }
    return fee
  }, [])

  // Fetch active fee schedule for selected wallet
  useEffect(() => {
    const fetchFeeSchedule = async () => {
      if (!selectedWalletId) {
        setFeeSchedule(null)
        return
      }

      setIsFeeLoading(true)
      try {
        const response = await apiClient.getWalletFeeSchedule(selectedWalletId)
        const data = (response as any).data?.data || (response as any).data
        setFeeSchedule(data || null)
      } catch (error) {
        console.error('Error fetching fee schedule:', error)
        setFeeSchedule(null)
      } finally {
        setIsFeeLoading(false)
      }
    }

    fetchFeeSchedule()
  }, [selectedWalletId])

  // Fetch exchange rate and convert currency in real-time
  useEffect(() => {
    const convertAmount = async () => {
      if (!amount || parseFloat(amount) <= 0 || !selectedWalletId) {
        setStdAmount(0)
        return
      }

      const amountValue = parseFloat(amount)
      if (isNaN(amountValue)) {
        setStdAmount(0)
        return
      }

      const selectedWallet = wallets?.find((w: any) => w.id === selectedWalletId)
      if (!selectedWallet) {
        setStdAmount(0)
        return
      }

      const targetCurrency = normalizeCurrencyCode(
        selectedWallet.currency_code || selectedWallet.currency
      )
      const apiCurrency = selectedCurrency
      setIsConverting(true)
      try {
        const response = await exchangeRateApi.convertCurrency(
          apiCurrency,
          targetCurrency,
          amountValue,
        )
        
        if (response.data.result === 'success') {
          const conversionResult = response.data.conversion_result || 
            (amountValue * response.data.conversion_rate)
          setStdAmount(conversionResult)
          setExchangeRate(response.data.conversion_rate)
        }
      } catch (error) {
        console.error('Error converting currency:', error)
        const fallbackRate = apiCurrency === 'EUR' && targetCurrency === 'STN'
          ? 24.5
          : 1
        setStdAmount(amountValue * fallbackRate)
        setExchangeRate(fallbackRate)
      } finally {
        setIsConverting(false)
      }
    }

    const timeoutId = setTimeout(convertAmount, 500)
    return () => clearTimeout(timeoutId)
  }, [amount, selectedCurrency, selectedWalletId, wallets])

  useEffect(() => {
    if (!stdAmount || stdAmount <= 0) {
      setFeeAmount(0)
      setNetAmount(0)
      return
    }

    const fee = calculateFee(stdAmount, feeSchedule)
    const net = Math.max(stdAmount - fee, 0)
    setFeeAmount(fee)
    setNetAmount(net)
  }, [stdAmount, feeSchedule, calculateFee])

  // Create payment intent when amount is valid
  useEffect(() => {
    const createPaymentIntent = async () => {
      if (!amount || parseFloat(amount) <= 0 || !selectedWalletId) {
        setClientSecret(null)
        setPaymentIntentId(null)
        return
      }

      const selectedWallet = wallets?.find((w: any) => w.id === selectedWalletId)
      if (!selectedWallet) {
        setClientSecret(null)
        setPaymentIntentId(null)
        return
      }

      const targetCurrency = normalizeCurrencyCode(
        selectedWallet.currency_code || selectedWallet.currency
      )

      try {
        const response = await apiClient.createPaymentIntent({
          amount: parseFloat(amount),
          currency: selectedCurrency,
          target_currency: targetCurrency,
          target_amount: stdAmount.toString(),
          wallet_id: selectedWalletId,
        })

        const data = (response as any).data?.data || (response as any).data
        const { client_secret, payment_intent_id } = data
        setClientSecret(client_secret)
        setPaymentIntentId(payment_intent_id)
      } catch (error: any) {
        console.error('Error creating payment intent:', error)
        setClientSecret(null)
        setPaymentIntentId(null)
        if (error.response?.data?.error) {
          toast.error(error.response.data.error)
        }
      }
    }

    const timeoutId = setTimeout(createPaymentIntent, 1000)
    return () => clearTimeout(timeoutId)
  }, [amount, stdAmount, selectedCurrency, selectedWalletId, wallets])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!stripe || !elements || !amount || parseFloat(amount) <= 0) {
      toast.error(t('wallet.pleaseEnterAmount') || 'Por favor, insira um valor')
      return
    }

    // Recreate payment intent if client secret is missing or expired
    let currentClientSecret = clientSecret
    let currentPaymentIntentId = paymentIntentId

    if (!currentClientSecret || !currentPaymentIntentId) {
      try {
        const selectedWallet = wallets?.find((w: any) => w.id === selectedWalletId)
        if (!selectedWallet) {
          toast.error(t('wallet.selectWallet') || 'Por favor, selecione uma carteira')
          return
        }

        setIsLoading(true)
        const response = await apiClient.createPaymentIntent({
          amount: parseFloat(amount),
          currency: selectedCurrency,
          target_currency: normalizeCurrencyCode(
            selectedWallet.currency_code || selectedWallet.currency,
          ),
          target_amount: stdAmount.toString(),
          wallet_id: selectedWalletId,
        })

        const data = (response as any).data?.data || (response as any).data
        currentClientSecret = data.client_secret
        currentPaymentIntentId = data.payment_intent_id
        setClientSecret(currentClientSecret)
        setPaymentIntentId(currentPaymentIntentId)
      } catch (error: any) {
        console.error('Error creating payment intent:', error)
        toast.error(error.response?.data?.error || t('wallet.depositError') || 'Erro ao criar pagamento')
        setIsLoading(false)
        return
      }
    }

    setIsLoading(true)

    try {
      const cardElement = elements.getElement(CardElement)
      if (!cardElement) {
        throw new Error('Card element not found')
      }

      if (!currentClientSecret) {
        throw new Error('Payment intent not available. Please try again.')
      }

      // Confirm payment without billing_details to avoid postal code issues
      // São Tomé e Príncipe doesn't use postal codes, and Stripe doesn't require billing_details
      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
        currentClientSecret,
        {
          payment_method: {
            card: cardElement,
          },
        }
      )

      if (confirmError) {
        // If payment intent not found, try to recreate it
        if (confirmError.code === 'payment_intent_unexpected_state' || 
            confirmError.message?.includes('No such payment_intent')) {
          toast.error(t('wallet.paymentIntentExpired') || 'O pagamento expirou. Por favor, tente novamente.')
          setClientSecret(null)
          setPaymentIntentId(null)
          return
        }
        
        throw new Error(confirmError.message || 'Payment failed')
      }

      if (paymentIntent && paymentIntent.status === 'succeeded') {
        const selectedWallet = wallets?.find((w: any) => w.id === selectedWalletId)
        const intentIdToUse = currentPaymentIntentId || paymentIntent.id
        
        try {
          await apiClient.confirmPayment({
            payment_intent_id: intentIdToUse,
            amount: parseFloat(amount),
            currency: selectedCurrency,
            std_amount: stdAmount.toString(),
            wallet_id: selectedWalletId,
          })

          toast.success(t('wallet.depositSuccess') || 'Depósito realizado com sucesso!')
          onSuccess()
          onClose()
        } catch (confirmError: any) {
          console.error('Error confirming payment on backend:', confirmError)
          // If payment succeeded on Stripe but backend confirmation failed,
          // the payment was still processed. Show a warning but don't block the user.
          const errorMsg = confirmError.response?.data?.error || confirmError.message
          if (errorMsg?.includes('No such payment_intent')) {
            toast.error(t('wallet.paymentIntentNotFound') || 'Erro ao confirmar pagamento. Por favor, contacte o suporte.')
          } else {
            toast.error(errorMsg || t('wallet.depositError') || 'Erro ao confirmar pagamento')
          }
        }
      } else {
        throw new Error(t('wallet.paymentNotCompleted') || 'Pagamento não foi completado')
      }
    } catch (error: any) {
      console.error('Payment error:', error)
      const errorMessage = error.message || t('wallet.depositError') || 'Erro ao processar pagamento'
      toast.error(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  const getCurrencySymbol = (currency: string) => {
    switch (normalizeCurrencyCode(currency)) {
      case 'EUR': return '€'
      case 'USD': return '$'
      case 'STN': return 'Db'
      default: return currency
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-text-primary mb-2">
          {t('wallet.selectCurrency', 'Selecione a moeda')}
        </label>
        <Select
          value={selectedCurrency}
          onChange={(value) => setSelectedCurrency(value as 'EUR' | 'USD')}
          options={[
            { value: 'EUR', label: 'EUR (€)' },
            { value: 'USD', label: 'USD ($)' }
          ]}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-text-primary mb-2">
          {t('wallet.amountToAdd')}
        </label>
        <div className="relative">
          <Input
            type="number"
            step="0.01"
            min="0.01"
            placeholder="0.00"
            value={amount}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAmount(e.target.value)}
            required
            className="pr-20"
          />
          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-text-secondary">
            {getCurrencySymbol(selectedCurrency)}
          </span>
        </div>
      </div>

      {wallets && wallets.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            {t('wallet.selectWallet')}
          </label>
          <Select
            value={selectedWalletId}
            onChange={(value) => setSelectedWalletId(value)}
            options={wallets.map((wallet: any) => ({
              value: wallet.id,
              label: `${wallet.currency_code || wallet.currency} - ${formatCurrency(wallet.balance, wallet.currency_code || wallet.currency)}`
            }))}
          />
        </div>
      )}

      {stdAmount > 0 && (
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-text-secondary">
              {t('wallet.equivalentInSTD')}
            </span>
            {isConverting && (
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
            )}
          </div>
          <div className="text-2xl font-bold text-text-primary">
            {formatCurrency(stdAmount, 'STN')}
          </div>
          <div className="mt-3 pt-3 border-t border-gray-200 space-y-2">
            <div className="flex items-center justify-between text-sm text-text-secondary">
              <span>
                {t('wallet.feeWithPercent', {
                  percent: feeSchedule?.cash_in_percent ?? 0,
                })}
              </span>
              <span>
                {isFeeLoading ? '...' : formatCurrency(feeAmount, feeSchedule?.currency_code || feeSchedule?.currency || 'STN')}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm font-semibold text-text-primary">
              <span>{t('wallet.netAmount')}</span>
              <span>
                {isFeeLoading ? '...' : formatCurrency(netAmount, feeSchedule?.currency_code || feeSchedule?.currency || 'STN')}
              </span>
            </div>
          </div>
          {exchangeRate !== 1 && (
            <div className="text-xs text-text-secondary mt-1">
              {t('wallet.exchangeRate')}: 1 {getCurrencySymbol(selectedCurrency)} ≈ {exchangeRate.toFixed(4)} Db
            </div>
          )}
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-text-primary mb-2">
          {t('wallet.cardDetails')}
        </label>
        <div className="border-2 border-gray-300 rounded-lg p-4 focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20">
          <CardElement
            options={{
              hidePostalCode: true,
              style: {
                base: {
                  fontSize: '16px',
                  color: '#1f2937',
                  '::placeholder': {
                    color: '#9ca3af',
                  },
                },
                invalid: {
                  color: '#ef4444',
                },
              },
            }}
          />
        </div>
      </div>

      <div className="flex space-x-3">
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          className="flex-1"
          disabled={isLoading}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('common.back')}
        </Button>
        <Button
          type="submit"
          className="flex-1"
          disabled={!stripe || isLoading || !amount || parseFloat(amount) <= 0 || !selectedWalletId}
        >
          {isLoading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              {t('wallet.processing')}
            </>
          ) : (
            t('wallet.addMoney')
          )}
        </Button>
      </div>
    </form>
  )
}

interface LocalStorePaymentProps {
  onSuccess: () => void
  onClose: () => void
  onBack: () => void
}

function LocalStorePayment({ onSuccess, onClose, onBack }: LocalStorePaymentProps) {
  const { t } = useTranslation()
  const [capturedImage, setCapturedImage] = useState<string | null>(null)
  const [capturedFile, setCapturedFile] = useState<File | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [isCameraActive, setIsCameraActive] = useState(false)
  
  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const isCameraStartingRef = useRef(false)

  const handleUpload = useCallback(async (file?: File) => {
    const fileToUpload = file || capturedFile
    if (!fileToUpload) return

    setIsUploading(true)

    try {
      await apiClient.uploadPaymentProof(fileToUpload)
      toast.success(t('wallet.paymentProofUploaded', 'Comprovante enviado com sucesso!'))
      onSuccess()
      onClose()
    } catch (error: any) {
      console.error('Error uploading payment proof:', error)
      toast.error(error.response?.data?.message || t('wallet.paymentProofError', 'Erro ao enviar comprovante'))
      setIsUploading(false)
    }
  }, [capturedFile, t, onSuccess, onClose])

  const startCamera = useCallback(async () => {
    if (isCameraStartingRef.current) {
      return
    }
    
    if (streamRef.current && streamRef.current.active) {
      return
    }
    
    try {
      isCameraStartingRef.current = true
      
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop())
        streamRef.current = null
      }
      
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: 'environment', // Back camera
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      })
      
      streamRef.current = mediaStream
      
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream
        setIsCameraActive(true)
        
        try {
          await videoRef.current.play()
        } catch (playError) {
          // Ignore play errors
        }
        
        await new Promise((resolve) => {
          if (videoRef.current) {
            const onLoadedMetadata = () => {
              videoRef.current?.removeEventListener('loadedmetadata', onLoadedMetadata)
              resolve(undefined)
            }
            videoRef.current.addEventListener('loadedmetadata', onLoadedMetadata)
            setTimeout(() => {
              videoRef.current?.removeEventListener('loadedmetadata', onLoadedMetadata)
              resolve(undefined)
            }, 1000)
          } else {
            resolve(undefined)
          }
        })
      }
    } catch (error: any) {
      console.error('Error accessing camera:', error)
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        toast.error(t('kyc.cameraPermissionDenied', 'Permissão da câmera negada. Por favor, permita o acesso à câmera nas configurações.'))
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        toast.error(t('kyc.cameraNotFound', 'Câmera não encontrada. Por favor, verifique se o dispositivo tem uma câmera disponível.'))
      } else {
        toast.error(t('kyc.cameraError', 'Erro ao acessar a câmera'))
      }
    } finally {
      isCameraStartingRef.current = false
    }
  }, [t])

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => {
        track.stop()
      })
      streamRef.current = null
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null
      videoRef.current.pause()
    }
    setIsCameraActive(false)
    isCameraStartingRef.current = false
  }, [])

  const capturePhoto = useCallback(() => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current
      const canvas = canvasRef.current
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      const ctx = canvas.getContext('2d')
      if (ctx) {
        ctx.drawImage(video, 0, 0)
        canvas.toBlob((blob) => {
          if (blob) {
            const file = new File([blob], 'payment-proof.jpg', { type: 'image/jpeg' })
            setCapturedFile(file)
            setCapturedImage(canvas.toDataURL('image/jpeg'))
            stopCamera()
            
            // Auto-upload immediately after capture
            handleUpload(file)
          }
        }, 'image/jpeg', 0.9)
      }
    }
  }, [stopCamera, handleUpload])

  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      setCapturedFile(file)
      const reader = new FileReader()
      reader.onload = (e) => {
        setCapturedImage(e.target?.result as string)
      }
      reader.readAsDataURL(file)
      handleUpload(file)
    }
  }, [handleUpload])

  useEffect(() => {
    if (!capturedImage) {
      const timer = setTimeout(() => {
        if (videoRef.current && !streamRef.current) {
          startCamera()
        }
      }, 300)
      return () => clearTimeout(timer)
    }
  }, [capturedImage, startCamera])

  useEffect(() => {
    return () => {
      stopCamera()
    }
  }, [stopCamera])

  return (
    <div className="space-y-6">
      {!capturedImage ? (
        <>
          <div className="relative bg-gray-100 dark:bg-gray-700 rounded-xl overflow-hidden aspect-video">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover"
              style={{ transform: 'scaleX(-1)' }}
            />
            {!isCameraActive && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-100 dark:bg-gray-700 z-10">
                <div className="text-center">
                  <Camera className="w-12 h-12 text-gray-400 mx-auto mb-2 animate-pulse" />
                  <p className="text-sm text-gray-500">{t('kyc.startingCamera', 'A iniciar câmera...')}</p>
                </div>
              </div>
            )}
            <canvas ref={canvasRef} className="hidden" />
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={onBack}
              className="flex-1"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('common.back')}
            </Button>
            <Button
              onClick={capturePhoto}
              className="flex-1"
            >
              <Camera className="w-4 h-4 mr-2" />
              {t('kyc.takePhoto', 'Tirar Foto')}
            </Button>
          </div>
          
          <p className="text-center text-sm text-text-secondary">
            {t('kyc.or', 'ou')}
          </p>
          
          <input
            type="file"
            accept="image/*,application/pdf"
            onChange={handleFileSelect}
            className="hidden"
            id="payment-proof-upload"
          />
          <label
            htmlFor="payment-proof-upload"
            className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors text-text-primary"
          >
            {t('kyc.uploadFile', 'Carregar Arquivo')}
          </label>
        </>
      ) : (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="space-y-4"
        >
          <img
            src={capturedImage}
            alt="Payment Proof"
            className="w-full h-auto rounded-xl object-contain max-h-60 border border-gray-200 dark:border-gray-700"
          />
          {isUploading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-6 h-6 animate-spin text-primary mr-2" />
              <span>{t('wallet.uploading', 'A enviar...')}</span>
            </div>
          ) : (
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setCapturedImage(null)
                  setCapturedFile(null)
                  startCamera()
                }}
                className="flex-1"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                {t('kyc.retakePhoto', 'Tirar Outra Foto')}
              </Button>
              <Button
                onClick={() => handleUpload()}
                className="flex-1"
                disabled={isUploading}
              >
                {t('wallet.send', 'Enviar')}
              </Button>
            </div>
          )}
        </motion.div>
      )}
    </div>
  )
}

interface MbReferencePaymentProps {
  onSuccess: () => void
  onBack: () => void
}

function MbReferencePayment({ onSuccess, onBack }: MbReferencePaymentProps) {
  const { t } = useTranslation()
  const [amount, setAmount] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [referenceData, setReferenceData] = useState<{
    reference_id: string
    entity: string
    reference: string
    amount: string
    status: string
    aeps_file?: string
    aeps_status?: string
    expires_at?: string | null
  } | null>(null)

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    const amountValue = Number.parseFloat(amount.replace(',', '.'))
    if (!Number.isFinite(amountValue) || amountValue <= 0) {
      toast.error(t('wallet.pleaseEnterAmount') || 'Por favor, insira um valor')
      return
    }

    setIsLoading(true)
    try {
      const idempotencyKey = crypto.randomUUID?.() || `${Date.now()}-${Math.random()}`
      const response = await apiClient.createMbReference({ amount: amountValue, idempotency_key: idempotencyKey })
      const data = (response as any).data?.data || (response as any).data
      setReferenceData(data)
      toast.success(t('wallet.mbReferenceCreated', 'Referência criada com sucesso'))
      onSuccess()
    } catch (error: any) {
      console.error('Error creating MB reference:', error)
      toast.error(error.response?.data?.error || t('wallet.mbReferenceError', 'Erro ao criar referência Multibanco'))
    } finally {
      setIsLoading(false)
    }
  }

  const copyReference = async () => {
    if (!referenceData) return
    const text = `Entidade: ${referenceData.entity}\nReferência: ${referenceData.reference}\nMontante: ${referenceData.amount} STN`
    try {
      await navigator.clipboard.writeText(text)
      toast.success(t('common.copied', 'Copiado'))
    } catch {
      toast.error(t('common.copyError', 'Não foi possível copiar'))
    }
  }

  if (referenceData) {
    return (
      <div className="space-y-5">
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-200 dark:divide-gray-700">
          <div className="p-4 flex items-center justify-between">
            <span className="text-sm text-text-secondary">{t('wallet.entity', 'Entidade')}</span>
            <span className="font-semibold text-text-primary tracking-wider">{referenceData.entity}</span>
          </div>
          <div className="p-4 flex items-center justify-between">
            <span className="text-sm text-text-secondary">{t('wallet.reference', 'Referência')}</span>
            <span className="font-semibold text-text-primary tracking-wider">{referenceData.reference}</span>
          </div>
          <div className="p-4 flex items-center justify-between">
            <span className="text-sm text-text-secondary">{t('wallet.amount', 'Montante')}</span>
            <span className="font-semibold text-text-primary">{formatCurrency(Number(referenceData.amount), 'STN')}</span>
          </div>
          <div className="p-4 flex items-center justify-between">
            <span className="text-sm text-text-secondary">{t('wallet.status', 'Estado')}</span>
            <span className="font-semibold text-text-primary">{referenceData.status}</span>
          </div>
        </div>

        <div className="flex gap-3">
          <Button type="button" variant="outline" onClick={onBack} className="flex-1">
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('common.back')}
          </Button>
          <Button type="button" onClick={copyReference} className="flex-1">
            <Copy className="h-4 w-4 mr-2" />
            {t('common.copy', 'Copiar')}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-text-primary mb-2">
          {t('wallet.amountToAdd')}
        </label>
        <div className="relative">
          <Input
            type="number"
            step="0.01"
            min="0.01"
            placeholder="0.00"
            value={amount}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setAmount(event.target.value)}
            required
            className="pr-20"
          />
          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-text-secondary">
            STN
          </span>
        </div>
      </div>

      <div className="flex gap-3">
        <Button type="button" variant="outline" onClick={onBack} className="flex-1" disabled={isLoading}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('common.back')}
        </Button>
        <Button type="submit" className="flex-1" disabled={isLoading || !amount || Number.parseFloat(amount) <= 0}>
          {isLoading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              {t('wallet.processing')}
            </>
          ) : (
            t('wallet.generateReference', 'Gerar referência')
          )}
        </Button>
      </div>
    </form>
  )
}

export function AddMoneyModal({ isOpen, onClose, userCurrency, wallets, onSuccess }: AddMoneyModalProps) {
  const { t } = useTranslation()
  const [isCreateWalletModalOpen, setIsCreateWalletModalOpen] = useState(false)
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('select')

  const hasWallets = wallets && wallets.length > 0

  const handleCreateWalletSuccess = () => {
    setIsCreateWalletModalOpen(false)
    if (onSuccess) {
      onSuccess()
    }
  }

  const handleClose = () => {
    setPaymentMethod('select')
    onClose()
  }

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={handleClose}
              className="fixed inset-0 bg-black/50 z-[100]"
            />

            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="fixed inset-0 z-[100] flex items-center justify-center p-4 pb-20 md:pb-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[calc(90vh-5rem)] md:max-h-[90vh] overflow-y-auto">
                <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                  <h2 className="text-xl font-bold text-text-primary">
                    {paymentMethod === 'select' ? t('wallet.addMoney') : 
                     paymentMethod === 'stripe' ? t('wallet.creditCard', 'Cartão de Crédito') :
                     paymentMethod === 'mb_reference' ? t('wallet.entityReference', 'Entidade/Referência') :
                     t('wallet.localStore', 'Posto Local')}
                  </h2>
                  <button
                    onClick={handleClose}
                    className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                  >
                    <X className="h-5 w-5 text-text-secondary" />
                  </button>
                </div>

                <div className="p-6">
                  {!hasWallets ? (
                    <div className="text-center py-8">
                      <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                        <Wallet className="h-8 w-8 text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold text-text-primary mb-2">
                        {t('wallet.createWallet')}
                      </h3>
                      <p className="text-text-secondary mb-6">
                        {t('wallet.createWalletFirst')}
                      </p>
                      <Button
                        onClick={() => {
                          setIsCreateWalletModalOpen(true)
                        }}
                        className="w-full"
                      >
                        {t('wallet.createWallet')}
                      </Button>
                    </div>
                  ) : paymentMethod === 'select' ? (
                    <div className="space-y-4">
                      <p className="text-text-secondary text-center mb-6">
                        {t('wallet.selectPaymentMethod', 'Selecione o método de pagamento')}
                      </p>

                      <button
                        onClick={() => setPaymentMethod('stripe')}
                        className="w-full p-5 border-2 border-primary rounded-xl hover:bg-primary/5 dark:hover:bg-primary/10 transition-all text-left group"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="p-3 bg-primary/10 rounded-lg group-hover:bg-primary/20">
                            <CreditCard className="w-6 h-6 text-primary" />
                          </div>
                          <div>
                            <div className="font-semibold text-text-primary text-lg group-hover:text-primary">
                              {t('wallet.creditCard', 'Cartão de Crédito')} 
                            </div>
                            <div className="text-sm text-text-secondary">
                              {t('wallet.creditCardDescription', 'Pague com cartão de crédito ou débito')}
                            </div>
                          </div>
                        </div>
                      </button>

                      <button
                        onClick={() => setPaymentMethod('local_store')}
                        className="w-full p-5 border-2 border-primary rounded-xl hover:bg-primary/5 dark:hover:bg-primary/10 transition-all text-left group"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="p-3 bg-primary/10 rounded-lg group-hover:bg-primary/20">
                            <Store className="w-6 h-6 text-primary" />
                          </div>
                          <div>
                            <div className="font-semibold text-text-primary text-lg group-hover:text-primary">
                              {t('wallet.localStore', 'Posto Local')}
                            </div>
                            <div className="text-sm text-text-secondary">
                              {t('wallet.localStoreDescription', 'Tire uma foto do comprovante de pagamento')}
                            </div>
                          </div>
                        </div>
                      </button>

                      <button
                        onClick={() => setPaymentMethod('mb_reference')}
                        className="w-full p-5 border-2 border-primary rounded-xl hover:bg-primary/5 dark:hover:bg-primary/10 transition-all text-left group"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="p-3 bg-primary/10 rounded-lg group-hover:bg-primary/20">
                            <Landmark className="w-6 h-6 text-primary" />
                          </div>
                          <div>
                            <div className="font-semibold text-text-primary text-lg group-hover:text-primary">
                              {t('wallet.entityReference', 'Entidade/Referência')}
                            </div>
                            <div className="text-sm text-text-secondary">
                              {t('wallet.entityReferenceDescription', 'Gere uma referência Multibanco para carregar em STN')}
                            </div>
                          </div>
                        </div>
                      </button>
                    </div>
                  ) : paymentMethod === 'stripe' && stripePromise ? (
                    <Elements 
                      stripe={stripePromise}
                      options={{
                        appearance: {
                          theme: 'stripe',
                        },
                      }}
                    >
                      <StripePaymentForm
                        wallets={wallets}
                        onSuccess={onSuccess || (() => {})}
                        onClose={handleClose}
                        onBack={() => setPaymentMethod('select')}
                      />
                    </Elements>
                  ) : paymentMethod === 'local_store' ? (
                    <LocalStorePayment
                      onSuccess={onSuccess || (() => {})}
                      onClose={handleClose}
                      onBack={() => setPaymentMethod('select')}
                    />
                  ) : paymentMethod === 'mb_reference' ? (
                    <MbReferencePayment
                      onSuccess={onSuccess || (() => {})}
                      onBack={() => setPaymentMethod('select')}
                    />
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-text-secondary">{t('wallet.stripeNotConfigured') || 'Stripe não configurado'}</p>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Create Wallet Modal */}
      <CreateWalletModal
        isOpen={isCreateWalletModalOpen}
        onClose={() => setIsCreateWalletModalOpen(false)}
        onSuccess={handleCreateWalletSuccess}
      />
    </>
  )
}
